import React, { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import {
  ExternalRealPropertyModel,
  RealPropertyModel,
} from '../../../../../../../../typings/api/skymap/rest/v1/project';
import EmptyStatePropertySeach from '../../../../../../../static/empty_state_property_search.svg?react';
import { SkyMapAxiosServiceFactory } from '../../../../../../services/axios/skymap-axios-service-factory';
import { useButtonHotkey } from '../../../../../hooks/use-button-hotkey';
import { useDialog } from '../../../../../hooks/use-dialog';
import { Button } from '../../../../button/button';
import { Icon } from '../../../../icon/icon';
import { IconPanel } from '../../../../icon-panel/icon-panel';
import { InfoBox } from '../../../../info-box/info-box';
import { LabelledContainer } from '../../../../labelled-container/labelled-container';
import { CollapsableSection } from '../../../../skyview/right-menu/components/collapsable-section';
import { Stack } from '../../../../stack/stack';
import { TextBox } from '../../../../text-box/text-box';
import { RealPropertyCard } from '../real-property-card';
import { PurchaseRealPropertyDialog } from './purchase-real-property-dialog';

type State = 'initial' | 'searched' | 'loading' | 'error';

export const SearchRealPropertiesSection = (props: {
  properties: RealPropertyModel[];
  addRealProperty: (property: RealPropertyModel) => void;
}) => {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(true);
  const [state, setState] = useState<State>('initial');
  const [searchText, setSearchText] = useState('');
  const { items, getProperties } = useRealPropertySearchResult(setState);
  const searchButtonRef = useRef(null);
  useButtonHotkey(searchButtonRef, 'Enter');

  const isLoading = state === 'loading';
  const isIdle = state === 'initial' || state === 'searched';

  return (
    <CollapsableSection
      expanded={expanded}
      title={t('onDemand.propertyBoundary.title', { ns: 'components' })}
      titleUpperCase={true}
      onSetExpanded={setExpanded}
    >
      <Stack spacing={1}>
        <LabelledContainer text={t('onDemand.propertyName', { ns: 'components' })}>
          <TextBox
            disabled={isLoading}
            placeholder={t('onDemand.propertyBoundary.placeholder', { ns: 'components' })}
            onChange={(e) => {
              setSearchText(e.target.value);
            }}
          />
        </LabelledContainer>

        <Button
          color="primary"
          disabled={isLoading || searchText.trim().length < 4}
          loading={isLoading}
          ref={searchButtonRef}
          variant="contained"
          width="100%"
          onClick={() => getProperties(searchText)}
        >
          {t('search', { ns: 'common' })}
        </Button>

        {state === 'error' && <ErrorState />}
        {isIdle && items.length > 0 && (
          <Result
            addRealProperty={props.addRealProperty}
            existingProjectRealProperties={props.properties}
            items={items}
          />
        )}
        {isIdle && items.length === 0 && <EmptyState state={state} />}
      </Stack>
    </CollapsableSection>
  );
};

const EmptyState = (props: { state: State }) => {
  const { t } = useTranslation();

  return (
    <IconPanel header={<EmptyStatePropertySeach />} responsiveness={{ compressed: false }}>
      {{
        title:
          props.state === 'searched'
            ? t('onDemand.propertyBoundary.emptyStateNoResultTitle', { ns: 'components' })
            : t('onDemand.propertyBoundary.emptyStateInitialTitle', { ns: 'components' }),
        info: t('onDemand.propertyBoundary.emptyStateInfo', { ns: 'components' }),
      }}
    </IconPanel>
  );
};

const ErrorState = () => {
  const { t } = useTranslation();

  return (
    <InfoBox color="red">{t('onDemand.propertyBoundary.error', { ns: 'components' })}</InfoBox>
  );
};

const Result = (props: {
  items: ExternalRealPropertyModel[];
  existingProjectRealProperties: RealPropertyModel[];
  addRealProperty: (property: RealPropertyModel) => void;
}) => {
  const { t } = useTranslation();
  const dialog = useDialog();
  const [selectedItem, setSelectedItem] = useState(props.items[0]);

  return (
    <OverflowY>
      <Stack spacing={0.4}>
        {props.items.map((item) => {
          const disabled = props.existingProjectRealProperties.some(
            (x) => x.externalId === item.externalId,
          );
          return (
            <RealPropertyCard disabled={disabled} item={item} key={item.externalId}>
              <Icon
                disabled={disabled}
                icon={['fal', 'shopping-cart']}
                title={t('openDialog', { ns: 'common' })}
                onClick={() => {
                  setSelectedItem(item);
                  dialog.show();
                }}
                onHoverStyle={{ icon: ['fad', 'shopping-cart'] }}
              />
            </RealPropertyCard>
          );
        })}
      </Stack>

      {dialog.render(
        <PurchaseRealPropertyDialog
          addRealProperty={props.addRealProperty}
          item={selectedItem}
          onClose={dialog.hide}
        />,
      )}
    </OverflowY>
  );
};

const useRealPropertySearchResult = (setState: (state: State) => void) => {
  const [items, setItems] = useState<ExternalRealPropertyModel[]>([]);

  const getProperties = useCallback(
    async (searchString: string) => {
      setState('loading');

      try {
        const response = await SkyMapAxiosServiceFactory.instance
          .createProjectServiceV1()
          .getRealProperties({
            query: {
              name: searchString,
            },
          });

        setItems(response.data);
        setState('searched');
      } catch {
        setState('error');
      }
    },
    [setState],
  );

  return { items, getProperties };
};

const OverflowY = styled.div`
  overflow-y: auto;
  max-height: 500px;
`;
